// Vista admin con dos pestañas: Productos (dimensiones) y Cajas (catálogo).
// Fase 11 — alimenta el asistente de packing 3D.

import { apiDel, apiGet, apiPatch, apiPost, apiPut } from '../api/client.js';
import { icon } from '../components/icons.js';
import { fontScaleButtonHTML } from '../components/font-scale.js';
import { openAlert } from '../components/modal.js';
import { navigate } from '../router.js';
import { getUser, logout } from '../state/auth.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

export function adminView({ tab = 'productos' } = {}) {
  return {
    async mount(root) {
      const user = getUser();
      const userName = user?.fullname || user?.username || 'Operario';

      root.innerHTML = `
        <div class="app">
          <header class="topbar">
            <button class="iconbtn" id="btn-back" title="Volver a pedidos" aria-label="Volver">
              ${icon('arrow-left', { size: 22 })}
            </button>
            <h1>Admin
              <span class="subtitle">${escapeHtml(userName)}</span>
            </h1>
            ${fontScaleButtonHTML()}
            <button class="iconbtn" id="btn-logout" title="Salir" aria-label="Salir">${icon('log-out', { size: 22 })}</button>
          </header>
          <nav class="admin-tabs">
            <button class="admin-tab ${tab === 'productos' ? 'active' : ''}" data-tab="productos">
              ${icon('ruler', { size: 18 })} Productos
            </button>
            <button class="admin-tab ${tab === 'cajas' ? 'active' : ''}" data-tab="cajas">
              ${icon('boxes', { size: 18 })} Cajas
            </button>
            <button class="admin-tab ${tab === 'usuarios' ? 'active' : ''}" data-tab="usuarios">
              ${icon('settings', { size: 18 })} Usuarios
            </button>
            <button class="admin-tab ${tab === 'logs' ? 'active' : ''}" data-tab="logs">
              ${icon('folder-tree', { size: 18 })} Logs
            </button>
            <button class="admin-tab ${tab === 'pendientes' ? 'active' : ''}" data-tab="pendientes">
              ${icon('alert-triangle', { size: 18 })} Pendientes
              <span id="pendientes-badge" class="badge --warn" style="display:none;margin-left:0.3rem;"></span>
            </button>
          </nav>
          <main class="content admin-content" id="admin-panel">
            <p style="color:var(--c-fg-muted);">Cargando…</p>
          </main>
        </div>`;

      root.querySelector('#btn-back').addEventListener('click', () => navigate('#/pedidos'));
      root.querySelector('#btn-logout').addEventListener('click', async () => {
        await logout();
        navigate('#/login', { replace: true });
      });
      root.querySelectorAll('.admin-tab').forEach(btn => {
        btn.addEventListener('click', () => {
          const t = btn.dataset.tab;
          navigate(`#/admin?tab=${t}`, { replace: true });
          renderPanel(t);
          root.querySelectorAll('.admin-tab').forEach(b => b.classList.toggle('active', b === btn));
        });
      });

      const panel = root.querySelector('#admin-panel');

      async function renderPanel(which) {
        panel.innerHTML = '<p style="color:var(--c-fg-muted);">Cargando…</p>';
        try {
          if (which === 'productos') await renderProductos(panel);
          else if (which === 'usuarios') await renderUsuarios(panel);
          else if (which === 'logs') await renderLogs(panel);
          else if (which === 'pendientes') await renderPendientes(panel);
          else await renderCajas(panel);
          // Tras cualquier render, actualizar el badge de pendientes
          actualizarBadgePendientes(root);
        } catch (e) {
          if (e?.status === 401) return;
          panel.innerHTML = `<div class="alert --error">
            <div class="ico">${icon('octagon-x', { size: 24 })}</div>
            <div class="body"><strong>Error</strong><span>${escapeHtml(e?.message || 'desconocido')}</span></div>
          </div>`;
        }
      }

      await renderPanel(tab);
      actualizarBadgePendientes(root);
    },
    unmount() {},
  };
}

async function actualizarBadgePendientes(root) {
  const badge = root.querySelector('#pendientes-badge');
  if (!badge) return;
  try {
    const lista = await apiGet('/admin/productos-pendientes?estado=pendiente');
    const n = lista.length;
    if (n > 0) {
      badge.textContent = n;
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }
  } catch { /* silencioso */ }
}

// =============================================================================
// Panel productos
// =============================================================================

async function renderProductos(host, { q = '' } = {}) {
  const params = new URLSearchParams({ pagina: '1', por_pagina: '200' });
  if (q) params.set('q', q);
  const data = await apiGet(`/admin/productos-dim?${params}`);

  const pendientes = data.items.filter(it => it.pendiente).length;
  const totales = data.items.length;

  host.innerHTML = `
    <div class="admin-toolbar">
      <div class="search-box">
        ${icon('search', { size: 18, cls: 'search-ico' })}
        <input id="prod-search" type="search" placeholder="Buscar por descripción, barcode o ID…" value="${escapeHtml(q)}"/>
      </div>
      <div class="counter">
        ${totales} productos · ${pendientes > 0
          ? `<span style="color:var(--c-warning,#b45309);">${pendientes} sin dimensiones</span>`
          : `<span style="color:var(--c-success,#15803d);">todos con dimensiones</span>`}
      </div>
    </div>
    <div class="admin-list" id="prod-list">
      ${data.items.length === 0
        ? `<p style="color:var(--c-fg-muted);padding:1rem;text-align:center;">No hay productos. Abre algún pedido para que aparezcan aquí.</p>`
        : data.items.map(rowHTML).join('')}
    </div>`;

  // Búsqueda con debounce
  let searchTimer = null;
  host.querySelector('#prod-search').addEventListener('input', (ev) => {
    clearTimeout(searchTimer);
    const valor = ev.target.value;
    searchTimer = setTimeout(() => renderProductos(host, { q: valor }), 300);
  });

  // Bind acciones por fila
  host.querySelectorAll('.prod-row').forEach(row => bindRow(row, host));
}

function rowHTML(it) {
  const pendienteBadge = it.pendiente
    ? `<span class="badge --warning">Pendiente</span>`
    : '';
  return `
    <div class="prod-row" data-pid="${it.producto_id}">
      <div class="prod-info">
        <div class="prod-title">
          ${escapeHtml(it.descripcion || `Producto ${it.producto_id}`)}
          ${pendienteBadge}
        </div>
        <div class="prod-sub">
          <span>ID ${it.producto_id}</span>
          ${it.barcode ? `<span>· ${escapeHtml(it.barcode)}</span>` : ''}
        </div>
      </div>
      <div class="prod-dims">
        <label>Alto<input type="number" min="1" max="5000" class="dim-input" data-field="alto_mm" value="${it.alto_mm ?? ''}" placeholder="mm"/></label>
        <label>Ancho<input type="number" min="1" max="5000" class="dim-input" data-field="ancho_mm" value="${it.ancho_mm ?? ''}" placeholder="mm"/></label>
        <label>Fondo<input type="number" min="1" max="5000" class="dim-input" data-field="fondo_mm" value="${it.fondo_mm ?? ''}" placeholder="mm"/></label>
        <label>Peso<input type="number" min="1" max="1000000" class="dim-input" data-field="peso_g" value="${it.peso_g ?? ''}" placeholder="g"/></label>
      </div>
      <div class="prod-flags">
        <label><input type="checkbox" class="flag-input" data-field="fragil" ${it.fragil ? 'checked' : ''}/> Frágil</label>
        <label><input type="checkbox" class="flag-input" data-field="no_rotar" ${it.no_rotar ? 'checked' : ''}/> No rotar</label>
      </div>
      <div class="prod-actions">
        <button class="btn --primary btn-save">${icon('check', { size: 16 })} Guardar</button>
        ${it.pendiente ? '' : `<button class="btn --ghost btn-clear" title="Limpiar dimensiones">${icon('trash-2', { size: 16 })}</button>`}
      </div>
    </div>`;
}

function bindRow(row, host) {
  const pid = parseInt(row.dataset.pid, 10);
  const getField = (field) => {
    const el = row.querySelector(`[data-field="${field}"]`);
    if (el.type === 'checkbox') return el.checked;
    return parseInt(el.value, 10);
  };

  row.querySelector('.btn-save').addEventListener('click', async (ev) => {
    const btn = ev.currentTarget;
    btn.disabled = true;
    try {
      const body = {
        alto_mm: getField('alto_mm'),
        ancho_mm: getField('ancho_mm'),
        fondo_mm: getField('fondo_mm'),
        peso_g: getField('peso_g'),
        fragil: getField('fragil'),
        no_rotar: getField('no_rotar'),
      };
      if (!body.alto_mm || !body.ancho_mm || !body.fondo_mm || !body.peso_g) {
        await openAlert({ tipo: 'error', titulo: 'Faltan campos', detalle: 'Rellena los 4 valores (alto, ancho, fondo, peso).' });
        return;
      }
      await apiPut(`/admin/productos-dim/${pid}`, body);
      btn.innerHTML = `${icon('check-circle', { size: 16 })} Guardado`;
      setTimeout(() => renderProductos(host, { q: host.querySelector('#prod-search')?.value || '' }), 600);
    } catch (e) {
      await openAlert({ tipo: 'error', titulo: 'No se pudo guardar', detalle: e.message });
    } finally {
      btn.disabled = false;
    }
  });

  const clearBtn = row.querySelector('.btn-clear');
  if (clearBtn) {
    clearBtn.addEventListener('click', async () => {
      try {
        await apiDel(`/admin/productos-dim/${pid}`);
        renderProductos(host, { q: host.querySelector('#prod-search')?.value || '' });
      } catch (e) {
        await openAlert({ tipo: 'error', titulo: 'No se pudo limpiar', detalle: e.message });
      }
    });
  }
}

// =============================================================================
// Panel cajas (tipos del catálogo)
// =============================================================================

async function renderCajas(host, { mostrarArchivadas = false } = {}) {
  const data = await apiGet(`/admin/cajas-tipo?solo_activas=${!mostrarArchivadas}`);

  host.innerHTML = `
    <div class="admin-toolbar">
      <button class="btn --primary" id="btn-nueva-caja">${icon('package-2', { size: 18 })} Nuevo tipo de caja</button>
      <label class="filter-toggle">
        <input type="checkbox" id="show-archived" ${mostrarArchivadas ? 'checked' : ''}/>
        Mostrar archivadas
      </label>
    </div>
    <div class="admin-list" id="cajas-list">
      ${data.length === 0
        ? `<p style="color:var(--c-fg-muted);padding:1rem;text-align:center;">${
            mostrarArchivadas
              ? 'No hay tipos de caja archivados.'
              : 'No hay tipos de caja activos. Crea al menos uno para que el asistente pueda sugerir packing.'
          }</p>`
        : data.map(cajaRowHTML).join('')}
    </div>`;

  host.querySelector('#btn-nueva-caja').addEventListener('click', () => abrirEditorCaja(host, null));
  host.querySelector('#show-archived').addEventListener('change', (ev) => {
    renderCajas(host, { mostrarArchivadas: ev.target.checked });
  });
  host.querySelectorAll('.caja-row').forEach(row => bindCajaRow(row, host, mostrarArchivadas));
}

function cajaRowHTML(c) {
  return `
    <div class="caja-row ${c.activa ? '' : '--archived'}" data-id="${c.id}">
      <div class="caja-info">
        <div class="caja-title">
          <strong>${escapeHtml(c.codigo)}</strong> · ${escapeHtml(c.nombre)}
          ${!c.activa ? '<span class="badge --neutral">Archivada</span>' : ''}
        </div>
        <div class="caja-sub">
          ${c.alto_interior_mm} × ${c.ancho_interior_mm} × ${c.fondo_interior_mm} mm interior ·
          peso máx ${c.peso_max_g} g · vacío ${c.peso_vacio_g} g
        </div>
      </div>
      <div class="caja-actions">
        <button class="btn --ghost btn-edit">${icon('pencil', { size: 16 })} Editar</button>
        ${c.activa
          ? `<button class="btn --ghost btn-archive">${icon('archive', { size: 16 })} Archivar</button>`
          : `<button class="btn --ghost btn-reactivate">${icon('rotate-cw', { size: 16 })} Reactivar</button>`}
      </div>
    </div>`;
}

function bindCajaRow(row, host, mostrarArchivadas) {
  const id = parseInt(row.dataset.id, 10);
  row.querySelector('.btn-edit')?.addEventListener('click', async () => {
    const data = await apiGet(`/admin/cajas-tipo?solo_activas=false`);
    const c = data.find(x => x.id === id);
    abrirEditorCaja(host, c);
  });
  row.querySelector('.btn-archive')?.addEventListener('click', async () => {
    try {
      await apiPatch(`/admin/cajas-tipo/${id}/archivar`);
      renderCajas(host, { mostrarArchivadas });
    } catch (e) {
      await openAlert({ tipo: 'error', titulo: 'Error', detalle: e.message });
    }
  });
  row.querySelector('.btn-reactivate')?.addEventListener('click', async () => {
    try {
      await apiPatch(`/admin/cajas-tipo/${id}/reactivar`);
      renderCajas(host, { mostrarArchivadas });
    } catch (e) {
      await openAlert({ tipo: 'error', titulo: 'Error', detalle: e.message });
    }
  });
}

async function abrirEditorCaja(host, cajaExistente) {
  const c = cajaExistente || {
    codigo: '', nombre: '',
    alto_interior_mm: '', ancho_interior_mm: '', fondo_interior_mm: '',
    peso_max_g: '', peso_vacio_g: 0, orden_display: 0,
  };
  const overlay = document.createElement('div');
  overlay.innerHTML = `
    <div class="scrim fade-in"><div class="modal" style="max-width: 420px;max-height:90dvh;overflow-y:auto;">
      <h2 style="margin-top:0;">${cajaExistente ? 'Editar' : 'Nuevo'} tipo de caja</h2>
      <div class="form-grid">
        <label>Código <input id="f-codigo" type="text" maxlength="16" value="${escapeHtml(c.codigo)}" placeholder="S, M, L, XL…"/></label>
        <label>Nombre <input id="f-nombre" type="text" maxlength="64" value="${escapeHtml(c.nombre)}" placeholder="Pequeña / Mediana…"/></label>
        <label>Alto interior (mm) <input id="f-alto" type="number" min="1" max="5000" value="${c.alto_interior_mm}"/></label>
        <label>Ancho interior (mm) <input id="f-ancho" type="number" min="1" max="5000" value="${c.ancho_interior_mm}"/></label>
        <label>Fondo interior (mm) <input id="f-fondo" type="number" min="1" max="5000" value="${c.fondo_interior_mm}"/></label>
        <label>Peso máximo (g) <input id="f-peso-max" type="number" min="1" value="${c.peso_max_g}"/></label>
        <label>Peso vacío (g) <input id="f-peso-vacio" type="number" min="0" value="${c.peso_vacio_g}"/></label>
        <label>Orden <input id="f-orden" type="number" min="0" value="${c.orden_display}"/></label>
      </div>
      <div class="actions">
        <button class="btn --ghost" id="btn-cancel">Cancelar</button>
        <button class="btn --primary" id="btn-save">Guardar</button>
      </div>
    </div></div>`;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.querySelector('#btn-cancel').addEventListener('click', close);

  overlay.querySelector('#btn-save').addEventListener('click', async (ev) => {
    const btn = ev.currentTarget;
    btn.disabled = true;
    try {
      const body = {
        codigo: overlay.querySelector('#f-codigo').value.trim(),
        nombre: overlay.querySelector('#f-nombre').value.trim(),
        alto_interior_mm: parseInt(overlay.querySelector('#f-alto').value, 10),
        ancho_interior_mm: parseInt(overlay.querySelector('#f-ancho').value, 10),
        fondo_interior_mm: parseInt(overlay.querySelector('#f-fondo').value, 10),
        peso_max_g: parseInt(overlay.querySelector('#f-peso-max').value, 10),
        peso_vacio_g: parseInt(overlay.querySelector('#f-peso-vacio').value, 10) || 0,
        orden_display: parseInt(overlay.querySelector('#f-orden').value, 10) || 0,
      };
      if (!body.codigo || !body.nombre || !body.alto_interior_mm || !body.ancho_interior_mm
          || !body.fondo_interior_mm || !body.peso_max_g) {
        await openAlert({ tipo: 'error', titulo: 'Faltan campos', detalle: 'Rellena código, nombre, dimensiones y peso máximo.' });
        return;
      }
      if (cajaExistente) {
        await apiPut(`/admin/cajas-tipo/${cajaExistente.id}`, body);
      } else {
        await apiPost(`/admin/cajas-tipo`, body);
      }
      close();
      renderCajas(host);
    } catch (e) {
      await openAlert({ tipo: 'error', titulo: 'No se pudo guardar', detalle: e.message });
    } finally {
      btn.disabled = false;
    }
  });
}

// =============================================================================
// Panel usuarios — Sub-fase A
// =============================================================================

async function renderUsuarios(host) {
  const data = await apiGet('/admin/usuarios');
  const me = getUser();

  host.innerHTML = `
    <div class="admin-toolbar">
      <button class="btn --primary" id="btn-nuevo-usuario">${icon('plus', { size: 18 })} Nuevo usuario</button>
      <span class="counter">${data.length} ${data.length === 1 ? 'usuario' : 'usuarios'}</span>
    </div>
    <div class="admin-list" id="usuarios-list">
      ${data.length === 0
        ? `<p style="color:var(--c-fg-muted);padding:1rem;text-align:center;">No hay usuarios.</p>`
        : data.map(u => userRowHTML(u, me?.user_id)).join('')}
    </div>`;

  host.querySelector('#btn-nuevo-usuario').addEventListener('click', () => abrirEditorUsuario(host));
  host.querySelectorAll('.user-row').forEach(row => bindUserRow(row, host, data, me?.user_id));
}

function userRowHTML(u, myId) {
  const isSelf = u.id === myId;
  const statusBadge = u.active
    ? (u.must_set_password ? '<span class="badge --warn">Pendiente set-pass</span>' : '')
    : '<span class="badge --neutral">Inactivo</span>';
  return `
    <div class="caja-row ${u.active ? '' : '--archived'}" data-uid="${u.id}">
      <div class="caja-info">
        <div class="caja-title">
          <strong>${escapeHtml(u.username)}</strong>
          ${u.fullname ? `· ${escapeHtml(u.fullname)}` : ''}
          <span class="badge ${u.role === 'admin' ? '--ok' : '--neutral'}">${u.role}</span>
          ${statusBadge}
          ${isSelf ? '<span class="badge --neutral">tú</span>' : ''}
        </div>
        <div class="caja-sub">
          Creado ${new Date(u.created_at).toLocaleDateString('es-ES')}
          ${u.last_login_at ? `· último login ${new Date(u.last_login_at).toLocaleDateString('es-ES')}` : '· nunca ha entrado'}
        </div>
      </div>
      <div class="caja-actions">
        <button class="btn --ghost btn-edit-user">${icon('pencil', { size: 16 })} Editar</button>
        <button class="btn --ghost btn-reset-pass" ${isSelf ? 'disabled title="No puedes resetearte a ti mismo desde aquí"' : ''}>${icon('rotate-cw', { size: 16 })} Resetear pass</button>
        ${u.active
          ? `<button class="btn --ghost btn-deactivate" ${isSelf ? 'disabled' : ''}>${icon('archive', { size: 16 })} Desactivar</button>`
          : `<button class="btn --ghost btn-reactivate-user">${icon('rotate-cw', { size: 16 })} Reactivar</button>`}
      </div>
    </div>`;
}

function bindUserRow(row, host, allUsers, myId) {
  const uid = parseInt(row.dataset.uid, 10);
  const user = allUsers.find(u => u.id === uid);
  if (!user) return;

  row.querySelector('.btn-edit-user').addEventListener('click', () => abrirEditorUsuario(host, user));

  row.querySelector('.btn-reset-pass')?.addEventListener('click', async (ev) => {
    if (ev.currentTarget.disabled) return;
    const ok = await openAlert({
      tipo: 'warn',
      titulo: `¿Resetear contraseña de "${user.username}"?`,
      detalle: 'El usuario tendrá que establecer una nueva la próxima vez que entre. Las sesiones activas siguen.',
      accion: 'Resetear',
      accion2: 'Cancelar',
    });
    if (ok !== 'primary') return;
    try {
      await apiPost(`/admin/usuarios/${uid}/reset-password`);
      renderUsuarios(host);
    } catch (e) {
      await openAlert({ tipo: 'error', titulo: 'No se pudo resetear', detalle: e.message });
    }
  });

  row.querySelector('.btn-deactivate')?.addEventListener('click', async (ev) => {
    if (ev.currentTarget.disabled) return;
    const ok = await openAlert({
      tipo: 'warn',
      titulo: `¿Desactivar "${user.username}"?`,
      detalle: 'No podrá entrar mientras esté inactivo. Su histórico de auditoría se conserva.',
      accion: 'Desactivar',
      accion2: 'Cancelar',
    });
    if (ok !== 'primary') return;
    try {
      await apiDel(`/admin/usuarios/${uid}`);
      renderUsuarios(host);
    } catch (e) {
      await openAlert({ tipo: 'error', titulo: 'No se pudo desactivar', detalle: e.message });
    }
  });

  row.querySelector('.btn-reactivate-user')?.addEventListener('click', async () => {
    try {
      await apiPatch(`/admin/usuarios/${uid}`, { active: true });
      renderUsuarios(host);
    } catch (e) {
      await openAlert({ tipo: 'error', titulo: 'No se pudo reactivar', detalle: e.message });
    }
  });
}

async function abrirEditorUsuario(host, existing = null) {
  const u = existing || { username: '', fullname: '', role: 'operario' };
  const isEdit = !!existing;
  const overlay = document.createElement('div');
  overlay.innerHTML = `
    <div class="scrim fade-in"><div class="modal" style="max-width: 420px;">
      <h2 style="margin-top:0;">${isEdit ? 'Editar' : 'Nuevo'} usuario</h2>
      ${isEdit ? '' : `<p class="hint" style="color:var(--c-fg-muted);font-size:var(--fs-sm);">Tras crearlo, el usuario tendrá que establecer su contraseña la primera vez que entre.</p>`}
      <div class="form-grid">
        <label style="grid-column: 1 / -1;">Usuario
          <input id="f-username" type="text" maxlength="64" pattern="[A-Za-z0-9._-]+"
                 value="${escapeHtml(u.username)}" ${isEdit ? 'readonly' : ''}
                 placeholder="maria.lopez"/>
        </label>
        <label style="grid-column: 1 / -1;">Nombre completo (opcional)
          <input id="f-fullname" type="text" maxlength="128" value="${escapeHtml(u.fullname || '')}"
                 placeholder="María López"/>
        </label>
        <label style="grid-column: 1 / -1;">Role
          <select id="f-role">
            <option value="operario" ${u.role === 'operario' ? 'selected' : ''}>Operario</option>
            <option value="admin" ${u.role === 'admin' ? 'selected' : ''}>Admin</option>
          </select>
        </label>
      </div>
      <div class="actions">
        <button class="btn --ghost" id="btn-cancel-user">Cancelar</button>
        <button class="btn --primary" id="btn-save-user">Guardar</button>
      </div>
    </div></div>`;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.querySelector('#btn-cancel-user').addEventListener('click', close);

  overlay.querySelector('#btn-save-user').addEventListener('click', async (ev) => {
    const btn = ev.currentTarget;
    btn.disabled = true;
    try {
      const username = overlay.querySelector('#f-username').value.trim();
      const fullname = overlay.querySelector('#f-fullname').value.trim() || null;
      const role = overlay.querySelector('#f-role').value;
      if (!username) {
        await openAlert({ tipo: 'error', titulo: 'Falta usuario', detalle: 'Introduce un nombre de usuario.' });
        return;
      }
      if (!/^[A-Za-z0-9._-]+$/.test(username)) {
        await openAlert({ tipo: 'error', titulo: 'Usuario inválido', detalle: 'Solo letras, números, punto, guión y guión bajo.' });
        return;
      }
      if (isEdit) {
        await apiPatch(`/admin/usuarios/${existing.id}`, { fullname, role });
      } else {
        await apiPost('/admin/usuarios', { username, fullname, role });
      }
      close();
      renderUsuarios(host);
    } catch (e) {
      let msg = e.message;
      if (e?.status === 409) msg = e.detail || 'Conflicto (¿usuario duplicado?)';
      await openAlert({ tipo: 'error', titulo: 'No se pudo guardar', detalle: msg });
    } finally {
      btn.disabled = false;
    }
  });
}

// =============================================================================
// Panel logs / auditoría — Sub-fase B
// =============================================================================

const ACCION_LABEL = {
  'caja.creada': 'Caja creada',
  'caja.anulada': 'Caja anulada',
  'caja.reemitida': 'Caja reemitida',
  'palet.creado': 'Palet creado',
  'palet.anulado': 'Palet anulado',
  'pedido.cerrado': 'Pedido cerrado',
  'envio.parcial': 'Envío parcial',
  'envio.definitivo': 'Envío definitivo',
  'override.añadida': 'Línea añadida',
  'override.modificada': 'Línea modificada',
  'override.eliminada': 'Línea eliminada',
  'shipment.crear': 'Shipment creado',
  'shipment.actualizar_extrafields': 'Shipment actualizado',
  'shipment.anular_y_recrear': 'Shipment recreado',
};

const ACCION_COLOR = {
  caja: '--ok', palet: '--ok', pedido: '--ok',
  envio: '--ok', override: '--warn', shipment: '--neutral',
};

// Cache de usuarios para el selector
let _usersCache = null;
let _accionesCache = null;

async function renderLogs(host, opts = {}) {
  const { pagina = 1, por_pagina = 50, user_id = '', accion = '', desde = '', hasta = '' } = opts;

  // Cargas paralelas la primera vez
  if (_usersCache === null) {
    _usersCache = await apiGet('/admin/usuarios');
  }
  if (_accionesCache === null) {
    _accionesCache = await apiGet('/admin/auditoria/acciones');
  }

  const params = new URLSearchParams({ pagina, por_pagina });
  if (user_id) params.set('user_id', user_id);
  if (accion) params.set('accion', accion);
  if (desde) params.set('desde', new Date(desde).toISOString());
  if (hasta) params.set('hasta', new Date(hasta).toISOString());

  const data = await apiGet(`/admin/auditoria?${params}`);

  const totalPaginas = Math.max(1, Math.ceil(data.total / por_pagina));

  host.innerHTML = `
    <div class="admin-toolbar">
      <select id="flt-user" style="min-height:36px;">
        <option value="">Todos los usuarios</option>
        ${_usersCache.map(u => `
          <option value="${u.id}" ${u.id == user_id ? 'selected' : ''}>${escapeHtml(u.username)}</option>
        `).join('')}
      </select>
      <select id="flt-accion" style="min-height:36px;">
        <option value="">Todas las acciones</option>
        ${_accionesCache.map(a => `
          <option value="${a}" ${a === accion ? 'selected' : ''}>${ACCION_LABEL[a] || a}</option>
        `).join('')}
      </select>
      <label style="font-size:var(--fs-xs);color:var(--c-fg-muted);display:flex;align-items:center;gap:0.3rem;">
        Desde
        <input id="flt-desde" type="date" value="${desde}" style="min-height:32px;"/>
      </label>
      <label style="font-size:var(--fs-xs);color:var(--c-fg-muted);display:flex;align-items:center;gap:0.3rem;">
        Hasta
        <input id="flt-hasta" type="date" value="${hasta}" style="min-height:32px;"/>
      </label>
      <button class="btn --ghost" id="btn-limpiar" style="min-height:36px;">Limpiar</button>
      <span class="counter">${data.total} ${data.total === 1 ? 'evento' : 'eventos'}</span>
    </div>

    ${data.items.length === 0
      ? `<p style="color:var(--c-fg-muted);padding:1rem;text-align:center;">Sin eventos con esos filtros.</p>`
      : `<div class="admin-list">${data.items.map(logRowHTML).join('')}</div>`}

    ${totalPaginas > 1 ? `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-top:1rem;padding:0.5rem;">
        <button class="btn --ghost" id="btn-prev" ${pagina === 1 ? 'disabled' : ''}>← Anterior</button>
        <span style="color:var(--c-fg-muted);font-size:var(--fs-sm);">Página ${pagina} de ${totalPaginas}</span>
        <button class="btn --ghost" id="btn-next" ${pagina >= totalPaginas ? 'disabled' : ''}>Siguiente →</button>
      </div>
    ` : ''}
  `;

  const reload = (changes = {}) => renderLogs(host, { pagina: 1, por_pagina, user_id, accion, desde, hasta, ...changes });

  host.querySelector('#flt-user').addEventListener('change', (e) => reload({ user_id: e.target.value }));
  host.querySelector('#flt-accion').addEventListener('change', (e) => reload({ accion: e.target.value }));
  host.querySelector('#flt-desde').addEventListener('change', (e) => reload({ desde: e.target.value }));
  host.querySelector('#flt-hasta').addEventListener('change', (e) => reload({ hasta: e.target.value }));
  host.querySelector('#btn-limpiar').addEventListener('click', () =>
    renderLogs(host, { pagina: 1, por_pagina }));
  host.querySelector('#btn-prev')?.addEventListener('click', () =>
    renderLogs(host, { pagina: pagina - 1, por_pagina, user_id, accion, desde, hasta }));
  host.querySelector('#btn-next')?.addEventListener('click', () =>
    renderLogs(host, { pagina: pagina + 1, por_pagina, user_id, accion, desde, hasta }));
}

function logRowHTML(e) {
  const ts = new Date(e.ts);
  const fechaCorta = ts.toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
  const color = ACCION_COLOR[e.target_kind] || '--neutral';
  const accionLabel = ACCION_LABEL[e.accion] || e.accion;
  const userPart = e.username
    ? `<strong>${escapeHtml(e.username)}</strong>`
    : `<span style="color:var(--c-fg-muted);">user #${e.user_id ?? '—'}</span>`;
  return `
    <div class="caja-row">
      <div class="caja-info">
        <div class="caja-title">
          <span class="badge ${color}">${escapeHtml(accionLabel)}</span>
          ${userPart}
          <span style="color:var(--c-fg-muted);font-size:var(--fs-xs);font-family:ui-monospace,monospace;">${fechaCorta}</span>
        </div>
        <div class="caja-sub">
          ${escapeHtml(e.target_label)}
          ${e.pedido_id ? `· <a href="#/pedido/${e.pedido_id}" style="color:var(--c-accent);">abrir pedido</a>` : ''}
        </div>
      </div>
    </div>`;
}

// =============================================================================
// Panel productos pendientes — Sub-fase C
// =============================================================================

async function renderPendientes(host, { estado = 'pendiente' } = {}) {
  const data = await apiGet(`/admin/productos-pendientes?estado=${estado}`);

  host.innerHTML = `
    <div class="admin-toolbar">
      <select id="flt-estado" style="min-height:36px;">
        <option value="pendiente" ${estado === 'pendiente' ? 'selected' : ''}>Pendientes</option>
        <option value="validado" ${estado === 'validado' ? 'selected' : ''}>Validados</option>
        <option value="rechazado" ${estado === 'rechazado' ? 'selected' : ''}>Rechazados</option>
      </select>
      <span class="counter">${data.length} ${data.length === 1 ? 'producto' : 'productos'}</span>
    </div>
    ${data.length === 0
      ? `<p style="color:var(--c-fg-muted);padding:1rem;text-align:center;">No hay productos en estado "${estado}".</p>`
      : `<div class="admin-list">${data.map(pendienteRowHTML).join('')}</div>`}
  `;

  host.querySelector('#flt-estado').addEventListener('change', (ev) =>
    renderPendientes(host, { estado: ev.target.value }));

  host.querySelectorAll('.pendiente-row').forEach(row => bindPendienteRow(row, host));
}

function pendienteRowHTML(p) {
  const propuesto = new Date(p.propuesto_at).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
  });
  const estadoBadge = {
    pendiente: '<span class="badge --warn">Pendiente</span>',
    validado: '<span class="badge --ok">Validado</span>',
    rechazado: '<span class="badge --neutral">Rechazado</span>',
  }[p.estado] || '';
  const acciones = p.estado === 'pendiente' ? `
    <div class="caja-actions">
      <button class="btn --primary btn-validar" data-pid="${p.id}">${icon('check', { size: 16 })} Validar</button>
      <button class="btn --danger btn-rechazar" data-pid="${p.id}">${icon('x', { size: 16 })} Rechazar</button>
    </div>
  ` : `
    <div class="caja-actions">
      ${p.producto_id_creado_erp ? `<span class="badge --neutral">ID ERP: ${p.producto_id_creado_erp}</span>` : ''}
    </div>
  `;
  const error = p.error_mensaje ? `
    <div class="aviso" style="margin:0.4rem 0;">${escapeHtml(p.error_mensaje)}</div>
  ` : '';
  return `
    <div class="caja-row pendiente-row" data-pid="${p.id}">
      <div class="caja-info">
        <div class="caja-title">
          <strong>${escapeHtml(p.descripcion)}</strong>
          ${estadoBadge}
        </div>
        <div class="caja-sub">
          ${p.barcode ? `EAN ${escapeHtml(p.barcode)} · ` : ''}
          ${p.alto_mm}×${p.ancho_mm}×${p.fondo_mm} mm · ${p.peso_g} g · cantidad ${p.cantidad}
          ${p.fragil ? ' · frágil' : ''}${p.no_rotar ? ' · no rotar' : ''}
        </div>
        <div class="caja-sub">
          Pedido ${p.pedido_id} · propuesto por <strong>${escapeHtml(p.propuesto_por_username || '?')}</strong> · ${propuesto}
        </div>
        ${error}
      </div>
      ${acciones}
    </div>`;
}

function bindPendienteRow(row, host) {
  const pid = row.dataset.pid;
  row.querySelector('.btn-validar')?.addEventListener('click', async (ev) => {
    const btn = ev.currentTarget;
    btn.disabled = true;
    try {
      const res = await apiPost(`/admin/productos-pendientes/${pid}/validar`);
      await openAlert({
        tipo: 'ok',
        titulo: 'Producto validado',
        detalle: `Creado en el ERP con ID ${res.producto_id_creado_erp}. ${res.overrides_actualizados} línea(s) actualizada(s) en el pedido.`,
      });
      renderPendientes(host);
    } catch (e) {
      await openAlert({
        tipo: 'error',
        titulo: 'No se pudo validar',
        detalle: e.detail || e.message || 'Error desconocido',
      });
      btn.disabled = false;
    }
  });

  row.querySelector('.btn-rechazar')?.addEventListener('click', async (ev) => {
    const ok = await openAlert({
      tipo: 'warn',
      titulo: '¿Rechazar este producto?',
      detalle: 'La línea desaparecerá del pedido. Esta acción es irreversible.',
      accion: 'Rechazar',
      accion2: 'Cancelar',
    });
    if (ok !== 'primary') return;
    try {
      await apiPost(`/admin/productos-pendientes/${pid}/rechazar`);
      renderPendientes(host);
    } catch (e) {
      await openAlert({
        tipo: 'error',
        titulo: 'No se pudo rechazar',
        detalle: e.detail || e.message,
      });
    }
  });
}
